/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../features/dashboard/dashboardSettings/DashboardSettingsAPI',
	'./DashboardSettingsUtils'
], (DashboardSettingsAPI, DashboardSettingsUtils) => {
	/**
	 * @class DashboardSettingsService
	 * @implements {@link DashboardSettingsAPI}
	 * @hideconstructor
	 * @classdesc providing an implementation of {@link DashboardSettingsAPI}
	 */
	const DashboardSettings = class DashboardSettings extends DashboardSettingsAPI {
		/**
		 *
		 * see DashboardSettings.create
		 */
		constructor({ globalConfig, applicationConfig, collectionsOverride, defaultImpl, apiConsumerConfig }) {
			super();
			this._init({ globalConfig, applicationConfig, collectionsOverride, defaultImpl, apiConsumerConfig });
		}

		_init({ globalConfig, applicationConfig, collectionsOverride, defaultImpl , apiConsumerConfig }) {
			this._settings = {};
			this._defaultImpl = defaultImpl;
			this._setConfigSettings(globalConfig);
			this._setGlassApplicationSettings(applicationConfig);
			this._setCollectionsSettings(applicationConfig, collectionsOverride);
			if (apiConsumerConfig){
				this._setConfigSettings(apiConsumerConfig);
			}
		}

		_setConfigSettings(globalConfig) {
			const keys = Object.keys(globalConfig);
			for (let j = 0; j < keys.length; j++) {
				this._settings[keys[j]] = globalConfig[keys[j]];
			}
		}

		_setGlassApplicationSettings(applicationConfig) {
			this._settings['applicationName'] = applicationConfig.label;
			this._settings['applicationSettings'] = applicationConfig.content;
			this._settings['applicationCssStyles'] = applicationConfig.cssStyles;
		}

		_setCollectionsSettings(applicationConfig, collectionsOverride) {
			applicationConfig.collectionContainers.forEach(container => {
				this._settings[container.id] = container.items || [];
				if (collectionsOverride && collectionsOverride[container.id]){
					this._settings[container.id] = this._settings[container.id].filter((entry) => {
						let exclude  = false;
						if (collectionsOverride[container.id].excludes) {
							exclude = collectionsOverride[container.id].excludes.indexOf(entry.name) !== -1;
						}
						let include = !exclude;
						if (collectionsOverride[container.id].items){
							include = include && !collectionsOverride[container.id].items.find(override => {
								return entry.name === override.name;
							});
						}
						return include;
					});
					if (collectionsOverride[container.id].items){
						collectionsOverride[container.id].items.forEach(entry => {
							if (entry.class){
								this._settings[container.id].push(entry);
							}
						});
					}
				}
				this._processingContainer(container);
			});
		}

		_processingContainer(container) {
			//TODO: revisit feature loading with dependencies for case like CADBC-2893
			if (container.id === 'com.ibm.bi.dashboard.features' ||
				container.id === 'com.ibm.bi.dashboard.content-features') {
				DashboardSettingsUtils.sortFeaturesByProfiles(container.items);
			}
		}

		get(name) {
			let setting = this._defaultImpl && this._defaultImpl.get(name);
			if (setting === undefined) {
				setting = this._settings[name];
			}
			return setting;
		}

		destroy() {
			delete this._defaultImpl;
			this._settings = {};
		}
	};

	/**
	 * factory create; allowing to mock the creation properly
	 * @param {Object} initParams - init parameters
	 * @param {Object} initParams.globalConfig - any config parameters coming from the glass config service
	 * @param {Object} initParams.applicationConfig - json of containing collections, cssStyles and options - TODO: schema to finalize
	 * @param {Object} initParams.collectionsOverride - overriden collections, used for features mainly
	 * @param {Object} initParams.defaultImpl - default implementation of the DashboardSettingsAPI
	 * @param {Object} initParams.apiConsumerConfig - setting provided by the API consumer that is building the API
	 */
	DashboardSettings.create = ({ globalConfig, applicationConfig, collectionsOverride, defaultImpl, apiConsumerConfig }) => {
		return new DashboardSettings({ globalConfig, applicationConfig, collectionsOverride, defaultImpl, apiConsumerConfig });
	};

	return DashboardSettings;
});